import React from "react";
import { GanttPointOptionsObject } from "highcharts";
import { GanttOperation, GanttResource, GanttCapacity, UpdateInnerGantt } from "../../common/communication.base";
import { NclInnerGantt } from "../../common/components.ncl";
import { __ } from "../../appcontext";

const emptyRow = "empty-row";

export interface ColorProps {
  AccentBaseColorBck: string;
  GridRulerColorBck: string;
  BaseColorBck1: string;
  ContentColorBck1: string;
  ContentChangeDecorateColorFrg: string;
  BaseColorFrg1: string;
  ContentFrame1: string;
  DataBrowseColorBck: string;
  DataBrowseColorFrg: string;
  DataChangeColorBck: string;
  DataChangeColorFrg: string;
}

export const getOperations = (items: any[][]) => {
  if (items == undefined) return [];

  const operations: GanttOperation[] = items.map((item): GanttOperation => {
    return {
      operationId: item[0],
      parentId: item[1],
      name: item[2],
      serieName: item[3],
      datetimeFrom: item[4],
      datetimeTo: item[5],
      completed: item[6],
      isMilestone: item[7],
      isStage: item[8],
      resourceId: item[9],
      resourceOrder: item[10],
      isExpanded: item[11],
      isInCollision: item[12],
      dependencyIds: item[13],
      dependencyTypes: item[14],
      customColor: item[15],
      workTime: item[16],
      workTimeUnit: item[17],
      htmlTooltip: item[18],
      description: item[19],
      customPattern: item[20],
      collisionRow: item[21],
      groupById: item[22],
      selected: item[23],
      readOnly: item[24],
    };
  });

  return operations;
};

export const getResources = (items: any[][]) => {
  if (items == undefined) return [];

  const resources: GanttResource[] = items.map((item): GanttResource => {
    return {
      resourceID: item[0],
      name: item[1],
      order: item[2],
      abbr: item[3],
      hasCollision: item[4],
      collisionCount: item[5],
    };
  });

  return resources;
};

export const getCapacities = (items: any[][]) => {
  if (items == undefined) return [];

  const capacities: GanttCapacity[] = items.map((item): GanttCapacity => {
    return {
      resourceID: item[0],
      order: item[1],
      datetimeFrom: item[2],
      datetimeTo: item[3],
      collisionRow: item[4],
      capacityID: item[5],
      capacityRowID: item[6],
      selected: item[7],
    };
  });

  return capacities;
};

interface GanttPoint extends Highcharts.GanttPointOptionsObject {
  borderColor?: string;
  borderWidth?: number;
  custom?: {
    workTime: number;
    workTimeUnit: string;
    htmlTooltip: string;
  };
}

export const getDataitems = (
  items: GanttOperation[],
  byResources: boolean,
  arrayOfCategories: string[],
  byOperations: boolean,
  ownColors: boolean,
  leftAxisTableColumns: string[],
  leftAxisTableData: string[][],
  clientZoom: number,
  vcxZoom: number,
  draggableX: boolean,
  draggableY: boolean,
  inEditMode: boolean,
  pointSelectAllowed: boolean,
  useInversePatternColors: boolean
): Highcharts.GanttPointOptionsObject[] => {
  const offset = 365 * 70 + 19;
  const day = 1000 * 60 * 60 * 24;

  const dataitems: Highcharts.GanttPointOptionsObject[] = items.map((item, itemindex): GanttPoint => {
    let itemdependencies: Array<Highcharts.XrangePointConnectorsOptionsObject>;

    let itemdependency_ids_string: string;
    itemdependency_ids_string = item.dependencyIds;
    const itemdependency_ids: string[] = itemdependency_ids_string.split(";");

    let itemdependency_types_string: string;
    itemdependency_types_string = item.dependencyTypes;
    const itemdependency_types: string[] = itemdependency_types_string.split(";");

    if (itemdependency_ids[0].trim().length > 0) {
      itemdependencies = [];

      for (let i = 0; i < itemdependency_ids.length; i++) {
        //if (itemdependency_ids[i].trim() == "") {
        //  continue;
        //}

        const dependentitem: Highcharts.XrangePointConnectorsOptionsObject = {};
        dependentitem.to = itemdependency_ids[i];
        // dependentitem.type = "fastAvoid"; //nejde

        switch (itemdependency_types[i]) {
          case "FS": //Task B starts after task A is finished
            //default
            break;

          case "FF": //Task B must end together with task A
            dependentitem.startMarker = {
              align: "right", // Where to start the line (horizontally)
              enabled: true, // Add the marker symbol
            };
            dependentitem.endMarker = {
              align: "right", // Where to end the line (horizontally)
            };
            break;

          case "SS": //Tasks A and B start together
            (dependentitem.startMarker = {
              align: "left", // Where to start the line (horizontally)
              enabled: true, // Add the marker symbol
            }),
              (dependentitem.endMarker = {
                align: "left", // Where to end the line (horizontally)
              });
            break;

          case "SF": //Task B must start before task A finishes
            (dependentitem.startMarker = {
              align: "right", // Where to start the line (horizontally)
              enabled: true, // Add the marker symbol
            }),
              (dependentitem.endMarker = {
                align: "left", // Where to end the line (horizontally)
              });
            break;

          default: //0
        }

        itemdependencies.push(dependentitem);
      }
    }

    const data = {
      collapsed: item.isExpanded ? false : true,
      id: item.operationId.toString(),
      name: item.name,
      description: item.description, //byResources ? item.name : item.description, //arrayOfCategories[item.resourceOrder],
      start: Math.round((item.datetimeFrom - offset) * day),
      end: Math.round((item.datetimeTo - offset) * day),
      completed: Math.round(item.completed) / 100,
      milestone: item.isMilestone,
      pointWidth: item.isStage ? 3 * clientZoom * vcxZoom : undefined,
      //borderColor: item.isInCollision ? "red" : null,
      //borderWidth: item.isInCollision ? 1 : null,
      // serieName: item[3],
      dependency: itemdependencies,
      custom: {
        workTime: item.workTime,
        workTimeUnit: item.workTimeUnit,
        htmlTooltip: item.htmlTooltip,
        readOnly: item.readOnly,
      },
      groupById: item.groupById, //dat do custom? pozor pak pri pouziti v dragDrop.groupBy
      dragDrop: {
        //draggableStart: false, u jednotlivych usecek neni
        //draggableEnd: false,
        draggableX: draggableX && inEditMode && !item.readOnly,
        draggableY: draggableY && inEditMode && !item.readOnly,
        //dragMinY: byResources ? 0 : 0,
        //dragMaxY: byResources ? arrayOfCategories.length - 1 : 0,
      },
      selected: pointSelectAllowed ? item.selected : false,
    };

    if (ownColors && item.customColor) {
      (data as any).color = item.customColor;
    } else {
      (data as any).color = defaultColors[itemindex % defaultColors.length];
    }

    //(data as any).states.select.color = (data as any).color;

    // pattern
    if (item.customPattern != 0) {
      let d: string;
      switch (item.customPattern) {
        case 1: //pada doprava
          d = "M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11";
          break;
        case 2: //doleva
          d = "M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11";
          break;
        case 3: //oba smery
          d = "M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11";
          break;
      }

      const lcolor = (data as any).color;
      (data as any).color = {
        pattern: {
          backgroundColor: lcolor,
          path: {
            d: d,
            stroke: useInversePatternColors ? getContrastingColor(lcolor) : "black",
            strokeWidth: 1.4,
          },
          width: 7,
          height: 7,
        },
      };
    }

    /*
    if (leftAxisTableData?.length > 1) {
      leftAxisTableData[item.resourceOrder].map((value, index) => {
        if (index === 0) return;

        data = {
          ...data,
          [leftAxisTableColumns[item.resourceOrder]]: value[index],
        };
      });
    }
    */

    if (byResources) {
      return {
        y: item.resourceOrder + item.collisionRow * 0.002,
        ...data,
      };
    } else if (byOperations) {
      return {
        y: itemindex,
        parent: item.parentId == 0 ? undefined : item.parentId.toString(),
        ...data,
      };
    }
  });

  return dataitems;
};

function getContrastingColor(hexColor: string) {
  // Odstraň '#' na začátku, pokud je přítomný
  hexColor = hexColor.replace("#", "");

  // Rozdělení hex barvy na RGB složky
  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);

  // Výpočet luminance
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  // Porovnání s prahovou hodnotou 128 (polovina mezi 0 a 255)
  return luminance > 128 ? "#000000" : "#FFFFFF";
}

export const getSeries = (
  operations: GanttOperation[],
  dataitems: GanttPointOptionsObject[],
  capacities: GanttCapacity[],
  //zoom: number,
  pointSelectAllowed: boolean,
  capacitiesColor: string
) => {
  const seriesdataitemsarray: string[] = [];
  let found: boolean;
  let serieName: string;
  for (let i = 0; i < operations.length; i++) {
    serieName = operations[i].serieName;
    found = false;
    for (let j = 0; j < seriesdataitemsarray.length; j++) {
      if (seriesdataitemsarray[j] == serieName) {
        found = true;
      }
    }
    if (!found) {
      seriesdataitemsarray.push(serieName);
    }
  }

  //2. dataitems do serii
  const seriesdataitems: Array<Highcharts.SeriesOptionsType> = []; // Highcharts.SeriesGanttOptions[] = [];
  for (let i = 0; i < seriesdataitemsarray.length; i++) {
    serieName = seriesdataitemsarray[i];

    const dataitemsforserie = new Array<Highcharts.GanttPointOptionsObject>();

    for (let j = 0; j < operations.length; j++) {
      if (serieName == operations[j].serieName) {
        dataitemsforserie.push(dataitems[j]);
      }
    }

    const seriesGanttOptions: Highcharts.SeriesOptionsType = {
      name: serieName,
      id: serieName,
      type: "gantt",
      zIndex: 10,
      data: dataitemsforserie,
    };

    seriesdataitems.push(seriesGanttOptions);
  }

  /* SRAFOVANI
  //https://www.highcharts.com/docs/chart-design-and-style/pattern-fills
  //https://jsfiddle.net/gh/get/library/pure/highcharts/highcharts/tree/master/samples/highcharts/css/pattern/
  let patternObject : Highcharts.PatternObject  = 
  {
    pattern: {
      aspectRatio : 1,
      backgroundColor : "#7c151c",
      color : "#7cb5ec",
      image: '',
      patternTransform : '',
      path: {
          d: 'M 0 0 L 10 10 M 9 -1 L 11 1 M -1 9 L 1 11',
          strokeWidth: 3
      },
      width: 10,
      height: 10,
      opacity: 0.4
    }
  }
  */

  if (capacities.length > 0) {
    //kapacity
    const capacitiesArray = new Array<Highcharts.XrangePointOptionsObject>();

    for (let i = 0; i < capacities.length; i++) {
      capacitiesArray.push({
        id: capacities[i].capacityRowID.toString(),
        name: capacities[i].capacityID.toString(),
        x: capacities[i].datetimeFrom * 1000,
        x2: capacities[i].datetimeTo * 1000,
        y: capacities[i].order + capacities[i].collisionRow * 0.002,
        //selected: capacities[i].selected,
        color: capacitiesColor, //"#FF0000",
      });
    }

    const seriesXrangeOptions: Highcharts.SeriesOptionsType = {
      name: "Kapacity",
      id: "capacities",
      type: "xrange",
      zIndex: 5,
      data: capacitiesArray,
    };

    seriesdataitems.push(seriesXrangeOptions);
  }

  return seriesdataitems;
};

export const getArrayOfCategories = (resources: GanttResource[]) => {
  if (resources == undefined) return [];

  const arrayOfCategories = resources.map((item) => item.abbr);

  // vkladam navic prazdny radek, protoze property breaks na ose Y nefunguje na poslednim radku
  // dochazelo tak k prekryvani usecek
  arrayOfCategories.push(emptyRow);

  return arrayOfCategories;
};

export const getYAxisOptions = (
  byOperations: boolean,
  byResources: boolean,
  arrayOfCategories: string[],
  resources: GanttResource[],
  operations: GanttOperation[],
  isDesktop: boolean,
  fontColor: string,
  leftAxisTableColumns: string[],
  clientZoom: number,
  vcxZoom: number,
  rowHeight: number | undefined
) => {
  let yAxisOptions: Highcharts.YAxisOptions;

  if (byOperations) {
    const categories = operations.map((item) => item.name);

    yAxisOptions = {
      type: "treegrid",
      staticScale: rowHeight ? rowHeight * vcxZoom : (isDesktop ? 22 : 21) * clientZoom * vcxZoom,
      categories: categories,
      labels: {
        enabled: true,
        style: {
          textDecoration: "none",
          color: fontColor,
        },
      },
      breaks: categories.map(() => {
        return {
          breakSize: 0,
          from: 0,
          to: 0,
        };
      }),
    };
  }

  //GRAF ZDROJU
  if (byResources) {
    //vysky radku pomoci breaks
    const lbreaks: Array<Highcharts.YAxisBreaksOptions> = [];
    for (const resource of resources) {
      if (resource.collisionCount > 0) {
        for (let i = 0; i < resource.collisionCount; i++) {
          const lbreak: Highcharts.YAxisBreaksOptions = {
            breakSize: 0.8, // Zbytečný padding na výšku
            from: resource.order + 0.001 + i * 0.002,
            to: resource.order + 0.001 + i * 0.002,
          };
          lbreaks.push(lbreak);
        }
      }
    }

    yAxisOptions = {
      type: "category",
      staticScale: rowHeight ? rowHeight * vcxZoom : 25 * clientZoom * vcxZoom, // Pokud je natvrdo nastavena výška řádku, jinak použije klientsky nastavenou
      labels: {
        enabled: true,
        style: {
          color: fontColor,
        },
        formatter: (context) => {
          const value = context.value as string;

          if (value === emptyRow) {
            return "";
          }
          /*
          for (const resource of resources) {
            const currentRow = resource.abbr === value;

            if (currentRow) {
              if (resource.hasCollision) {
                //return `<span style="color: red">${value}</span>`; zatim nebarvim -> TODOKUF bude to jina informace
              }
            }
          }
          */
          return value;
        },
      },
      categories: arrayOfCategories,
      breaks: lbreaks,
      min: 0,
      max: arrayOfCategories.length - 1,
      showEmpty: true,
      startOfWeek: 1,
      /*
      grid:{
        columns: [{
          title: {
            text: ''
          },
          labels: {
            format: '{point.name}'
          }
          }, {
          title: {
            text: 'Kapacita'
          },
          labels: {
            format: '{point.CapacityUMCalc}'
          }
          }, {
          title: {
            text: 'Požadavky'
          },
          labels: {
            format: '{point.RequirementsUMCalc}'
          }
        }]
      }
*/
    };

    /*
    if (leftAxisTableColumns?.length > 1) {

      let columns: XAxisOptions[] = [];
      columns = leftAxisTableColumns.map((title) => {
        return {
          title: {
            text: title,
          },
          labels: {
            format: "{point.name}",
          },
        };
      });

      yAxisOptions.grid.columns = columns;
    }
    */
  }

  return yAxisOptions;
};

export const updateDataAfterDrop = (e: Highcharts.PointDropEventObject) => {
  const updatedData: string[][] = [];
  const newPoints: Highcharts.Dictionary<Highcharts.PointDragDropObject> = e.newPoints;

  for (const id in newPoints) {
    const changes: Highcharts.PointDragDropObject = newPoints[id];

    for (const key in changes.newValues) {
      let value = changes.newValues[key];

      if (key === "end") {
        value = new Date(value).setSeconds(new Date(value).getSeconds() - 2);
      }

      updatedData.push([id, key, value.toString()]);
    }
  }

  return updatedData;
};

export const NoData = (colors: ColorProps) => {
  if (!colors) return null;

  return (
    <div
      style={{
        alignItems: "center",
        height: "100%",
        width: "100%",
        background: colors.DataBrowseColorBck,
      }}
      className="no_data"
    >
      <div
        style={{
          display: "flex",
          flex: "1 1 auto",
          background: colors.ContentFrame1,
          color: colors.DataBrowseColorFrg,
          padding: "3px",
          justifyContent: "center",
        }}
      >
        {__("noDataToView")}
      </div>
    </div>
  );
};

const defaultColors = ["#7cb5ec", "#434348", "#90ed7d", "#f7a35c", "#8085e9", "#f15c80", "#e4d354", "#2b908f", "#f45b5b", "#91e8e1"];

export const getPlotBands = (days: number[][]) => {
  return days.map((day) => {
    let color = "";

    if (day[0] === 1) {
      color = "#eee";
    }

    if (day[0] === 2) {
      color = "salmon";
    }

    return {
      from: day[1] * 1000,
      to: day[2] * 1000,
      color: color,
      zIndex: 1,
    };
  });
};

export function initGanttData(data: UpdateInnerGantt, control: NclInnerGantt, colors: any, clientZoom: number) {
  const operations = getOperations(data.Operations.toJS() as any[][]);
  const resources = getResources(data.Resources.toJS());
  const capacities = getCapacities(data.Capacities.toJS());
  const arrayOfCategories = getArrayOfCategories(resources);
  const yAxisOptions = getYAxisOptions(
    control.Container.byOperations(),
    control.Container.byResources(),
    arrayOfCategories,
    resources,
    operations,
    false,
    colors.DataBrowseColorFrg,
    control.Container.LeftAxisTableColumns,
    clientZoom,
    control.VCXZoom,
    control.Container?.Ncl?.FrgtData?.RowHeight
  );
  const dataitems = getDataitems(
    operations,
    control.Container.byResources(),
    arrayOfCategories,
    control.Container.byOperations(),
    control.Container.ownColors(),
    control.Container.LeftAxisTableColumns,
    control.Container.LeftAxisTableData,
    clientZoom,
    control.VCXZoom,
    data.DraggableX,
    data.DraggableY,
    control.InEditMode,
    control.Container.pointSelectAllowed(),
    control.Container?.Ncl?.FrgtData?.UseInversePatternColors ?? false
  );
  const seriesdataitems = getSeries(operations, dataitems, capacities /*, control.Zoom*/, control.Container.pointSelectAllowed(), control.CapacitiesColor);
  const plotBands = getPlotBands(data.DayBackgrounds);

  control.DataItems = dataitems;
  control.SeriesDataItems = seriesdataitems;
  control.YAxisOptions = yAxisOptions;
  control.Categories = yAxisOptions?.categories;
  control.DraggableX = data.DraggableX;
  control.DraggableY = data.DraggableY;
  control.HideHint = data.HideHint;
  control.XAxisOptions = {
    minRangeDate: data.MinRangeDate,
    maxRangeDate: data.MaxRangeDate,
    plotBands: plotBands,
  };
}
